// ------------------------------------------------------------------------------
// <copyright file="ApiTestHarness.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Activity, Lock, Radio, MessageCircle, CreditCard, BookOpen,
    Brain, Shield, Wifi, Building, Terminal, Search, ChevronDown,
    ChevronRight, Copy, Check, AlertTriangle, AlertCircle,
    RefreshCw, Play, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import API_GROUPS, { ENVIRONMENTS, generateSessionId, generateUUID } from '../config/apiHarnessConfig';
import './ApiTestHarness.css';

// ---------------------------------------------------------------------------
// Icon map — resolves icon name string from config to lucide-react component
// ---------------------------------------------------------------------------
const ICON_MAP = {
    Activity, Lock, Radio, MessageCircle, CreditCard,
    BookOpen, Brain, Shield, Wifi, Building,
};

function GroupIcon({ name, size = 16 }) {
    const Icon = ICON_MAP[name] || Activity;
    return <Icon size={size} />;
}

// ---------------------------------------------------------------------------
// Method badge colors
// ---------------------------------------------------------------------------
const METHOD_CLASSES = {
    GET: 'method-get',
    POST: 'method-post',
    PUT: 'method-put',
    DELETE: 'method-delete',
    PATCH: 'method-patch',
};

// ---------------------------------------------------------------------------
// JSON syntax highlighter
// ---------------------------------------------------------------------------
function highlightJson(json) {
    if (typeof json !== 'string') {
        json = JSON.stringify(json, null, 2);
    }
    return json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
            (match) => {
                let cls = 'json-number';
                if (/^"/.test(match)) {
                    cls = /:$/.test(match) ? 'json-key' : 'json-string';
                } else if (/true|false/.test(match)) {
                    cls = 'json-bool';
                } else if (/null/.test(match)) {
                    cls = 'json-null';
                }
                // SAFETY: match is already HTML-escaped above (& < > replaced).
                // cls is a hardcoded constant — never derived from external data.
                return `<span class="${cls}">${match}</span>`;
            }
        );
}

// ---------------------------------------------------------------------------
// Build URL from endpoint + params + environment
// ---------------------------------------------------------------------------
function buildUrl(baseUrl, endpoint, paramValues) {
    let path = endpoint.path;

    // Substitute route params
    endpoint.params
        .filter(p => p.location === 'route')
        .forEach(p => {
            path = path.replace(`{${p.name}}`, encodeURIComponent(paramValues[p.name] || ''));
        });

    const url = new URL(baseUrl + path);

    // Append query params
    endpoint.params
        .filter(p => p.location === 'query' && paramValues[p.name] !== '' && paramValues[p.name] !== undefined)
        .forEach(p => {
            url.searchParams.set(p.name, paramValues[p.name]);
        });

    return url.toString();
}

// ---------------------------------------------------------------------------
// Build request body from params
// ---------------------------------------------------------------------------
function buildBody(endpoint, paramValues) {
    const bodyParams = endpoint.params.filter(p => p.location === 'body');
    if (bodyParams.length === 0) return null;

    const body = {};
    bodyParams.forEach(p => {
        const val = paramValues[p.name];
        if (val === '' || val === undefined) return;
        if (p.type === 'json') {
            try { body[p.name] = JSON.parse(val); } catch { body[p.name] = val; }
        } else if (p.type === 'number') {
            body[p.name] = val === '' ? undefined : Number(val);
        } else if (p.type === 'boolean') {
            body[p.name] = val === true || val === 'true';
        } else {
            body[p.name] = val;
        }
    });
    return body;
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
    let cls = 'status-2xx';
    if (status >= 400 && status < 500) cls = 'status-4xx';
    else if (status >= 500) cls = 'status-5xx';
    else if (status >= 300) cls = 'status-3xx';
    return <span className={`status-badge ${cls}`}>{status}</span>;
}

// ---------------------------------------------------------------------------
// CopyButton
// ---------------------------------------------------------------------------
function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [text]);

    return (
        <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
            {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
    );
}

// ---------------------------------------------------------------------------
// ParamInput — renders the right input for a param type
// ---------------------------------------------------------------------------
function ParamInput({ param, value, onChange }) {
    const handleGenerate = useCallback(() => {
        const id = param.generate === 'uuid' ? generateUUID() : generateSessionId();
        onChange(id);
    }, [param.generate, onChange]);

    if (param.type === 'boolean') {
        return (
            <button
                className={`bool-toggle ${value ? 'bool-on' : 'bool-off'}`}
                onClick={() => onChange(!value)}
                type="button"
            >
                <span className="bool-knob" />
                <span className="bool-label">{value ? 'true' : 'false'}</span>
            </button>
        );
    }

    if (param.type === 'select') {
        return (
            <select
                aria-label={param.name}
                className="param-input param-select"
                value={value}
                onChange={e => onChange(e.target.value)}
            >
                {(param.options || []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        );
    }

    if (param.type === 'json' || param.type === 'textarea') {
        return (
            <textarea
                className="param-input param-textarea"
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={param.placeholder || ''}
                rows={param.type === 'json' ? 4 : 3}
                spellCheck={false}
            />
        );
    }

    return (
        <div className="param-input-row">
            <input
                className="param-input"
                type={param.type === 'number' ? 'number' : 'text'}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={param.placeholder || ''}
            />
            {param.generate && (
                <button
                    className="generate-btn"
                    onClick={handleGenerate}
                    title={`Generate ${param.generate}`}
                    type="button"
                >
                    <RefreshCw size={13} />
                </button>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// EndpointCard
// ---------------------------------------------------------------------------
function EndpointCard({ endpoint, environment, functionKey }) {
    // Initialize param values from defaults
    const initValues = () => {
        const vals = {};
        endpoint.params.forEach(p => {
            vals[p.name] = p.default !== undefined ? p.default : (p.type === 'boolean' ? false : '');
        });
        return vals;
    };

    const [expanded, setExpanded] = useState(false);
    const [paramValues, setParamValues] = useState(initValues);
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const updateParam = useCallback((name, val) => {
        setParamValues(prev => ({ ...prev, [name]: val }));
    }, []);

    const execute = useCallback(async () => {
        const url = buildUrl(environment.url, endpoint, paramValues);
        const body = buildBody(endpoint, paramValues);

        const headers = { 'Content-Type': 'application/json' };
        // Some routes need BOTH: AuthorizationLevel.Function gates the host, and the
        // function itself then reads the caller's tenant off the JWT. Sending one and
        // omitting the other is a 401 with no clue which credential was missing.
        const usesFunctionKey = endpoint.auth === 'function-key' || endpoint.auth === 'function-key+jwt';
        if (usesFunctionKey && functionKey) {
            headers['x-functions-key'] = functionKey;
        }

        // Merge header-location params into the request headers
        endpoint.params
            .filter(p => p.location === 'header' && paramValues[p.name])
            .forEach(p => { headers[p.name] = paramValues[p.name]; });

        const needsJwt = endpoint.auth === 'jwt' || endpoint.auth === 'function-key+jwt';

        setLoading(true);
        setResponse(null);

        const start = performance.now();
        try {
            let res;
            if (needsJwt) {
                // JWT auth via fetchWithAuth
                res = await fetchWithAuth(url, {
                    method: endpoint.method,
                    headers,
                    body: body ? JSON.stringify(body) : undefined,
                }, { skipAuth: false });
            } else {
                // No JWT — anonymous or function-key (key already in headers above)
                res = await fetch(url, {
                    method: endpoint.method,
                    headers,
                    body: body ? JSON.stringify(body) : undefined,
                });
            }

            const elapsed = Math.round(performance.now() - start);
            let responseBody;
            const contentType = res.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                responseBody = await res.json();
            } else {
                responseBody = await res.text();
            }

            setResponse({
                status: res.status,
                elapsed,
                body: responseBody,
                url,
                method: endpoint.method,
            });
        } catch (err) {
            const elapsed = Math.round(performance.now() - start);
            setResponse({
                status: 0,
                elapsed,
                body: { error: err.message },
                url,
                method: endpoint.method,
            });
        } finally {
            setLoading(false);
        }
    }, [endpoint, environment.url, functionKey, paramValues]);

    const handleExecuteClick = useCallback(() => {
        if (endpoint.dangerLevel === 'critical') {
            setConfirming(true);
        } else {
            execute();
        }
    }, [endpoint.dangerLevel, execute]);

    const confirmExecute = useCallback(() => {
        setConfirming(false);
        execute();
    }, [execute]);

    const isDanger = endpoint.dangerLevel === 'critical';
    const isWarning = endpoint.dangerLevel === 'warning';

    return (
        <div className={`endpoint-card ${isDanger ? 'endpoint-danger' : ''} ${isWarning ? 'endpoint-warning' : ''}`}>
            {/* Header row — always visible */}
            <button
                className="endpoint-header"
                onClick={() => setExpanded(e => !e)}
                type="button"
            >
                <span className={`method-badge ${METHOD_CLASSES[endpoint.method] || 'method-get'}`}>
                    {endpoint.method}
                </span>
                <span className="endpoint-path">{endpoint.path}</span>
                <span className="endpoint-name">{endpoint.name}</span>
                {endpoint.dangerLevel === 'critical' && (
                    <span className="danger-pill"><AlertCircle size={12} /> Critical</span>
                )}
                {endpoint.dangerLevel === 'warning' && (
                    <span className="warning-pill"><AlertTriangle size={12} /> Warning</span>
                )}
                <span className="endpoint-chevron">
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
            </button>

            {/* Expanded content */}
            {expanded && (
                <div className="endpoint-body">
                    {endpoint.description && (
                        <p className="endpoint-description">{endpoint.description}</p>
                    )}

                    {/* Danger warning banner */}
                    {endpoint.dangerLevel && (
                        <div className={`danger-banner ${endpoint.dangerLevel === 'critical' ? 'danger-banner-critical' : 'danger-banner-warning'}`}>
                            {endpoint.dangerLevel === 'critical'
                                ? <AlertCircle size={14} />
                                : <AlertTriangle size={14} />
                            }
                            <span>{endpoint.dangerMessage}</span>
                        </div>
                    )}

                    {/* Auth badge */}
                    {endpoint.auth && (
                        <div className="auth-badge">
                            <Lock size={12} />
                            {endpoint.auth === 'function-key' ? 'Function Key' : endpoint.auth === 'jwt' ? 'JWT' : endpoint.auth === 'function-key+jwt' ? 'Function Key + JWT' : endpoint.auth}
                        </div>
                    )}

                    {/* Parameter form */}
                    {endpoint.params.length > 0 && (
                        <div className="param-form">
                            <div className="param-form-header">Parameters</div>
                            {endpoint.params.map(param => (
                                <div key={param.name} className="param-row">
                                    <div className="param-meta">
                                        <span className="param-name">{param.name}</span>
                                        <span className={`param-location loc-${param.location}`}>{param.location}</span>
                                        {param.required && <span className="param-required">required</span>}
                                    </div>
                                    <ParamInput
                                        param={param}
                                        value={paramValues[param.name]}
                                        onChange={val => updateParam(param.name, val)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Execute button */}
                    {confirming ? (
                        <div className="confirm-dialog">
                            <span className="confirm-text">
                                <AlertCircle size={14} /> Execute this critical operation?
                            </span>
                            <button className="btn-confirm-yes" onClick={confirmExecute} type="button">
                                Yes, execute
                            </button>
                            <button className="btn-confirm-no" onClick={() => setConfirming(false)} type="button">
                                <X size={13} /> Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            className={`execute-btn ${isDanger ? 'execute-danger' : isWarning ? 'execute-warning' : 'execute-default'}`}
                            onClick={handleExecuteClick}
                            disabled={loading}
                            type="button"
                        >
                            {loading ? (
                                <><RefreshCw size={14} className="spin" /> Running...</>
                            ) : (
                                <><Play size={14} /> Execute</>
                            )}
                        </button>
                    )}

                    {/* Response panel */}
                    {response && (
                        <div className="response-panel">
                            <div className="response-meta">
                                <StatusBadge status={response.status} />
                                <span className="response-time">{response.elapsed}ms</span>
                                <span className="response-url">{response.method} {response.url}</span>
                                <CopyButton
                                    text={typeof response.body === 'string'
                                        ? response.body
                                        : JSON.stringify(response.body, null, 2)}
                                />
                            </div>
                            <pre
                                className="response-body"
                                dangerouslySetInnerHTML={{
                                    __html: highlightJson(
                                        typeof response.body === 'string'
                                            ? response.body
                                            : JSON.stringify(response.body, null, 2)
                                    )
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Group section
// ---------------------------------------------------------------------------
function GroupSection({ group, environment, functionKey, groupRef }) {
    return (
        <section className="group-section" ref={groupRef}>
            <div className="group-header">
                <span className="group-icon-wrap">
                    <GroupIcon name={group.icon} size={18} />
                </span>
                <div>
                    <h2 className="group-title">{group.name}</h2>
                    {group.description && (
                        <p className="group-description">{group.description}</p>
                    )}
                </div>
                <span className="group-count">{group.endpoints.length}</span>
            </div>
            <div className="endpoint-list">
                {group.endpoints.map(ep => (
                    <EndpointCard
                        key={ep.id}
                        endpoint={ep}
                        environment={environment}
                        functionKey={functionKey}
                    />
                ))}
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function ApiTestHarness() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [envId, setEnvId] = useState('local');
    const [functionKey, setFunctionKey] = useState('');
    const [search, setSearch] = useState('');
    const [activeGroup, setActiveGroup] = useState(API_GROUPS[0]?.id);

    const groupRefs = useRef({});

    // SuperAdmin guard
    const isSuperAdmin = user?.IsPlatformAdmin || user?.Role === 'SuperAdmin';
    useEffect(() => {
        if (!isSuperAdmin) {
            navigate('/');
        }
    }, [isSuperAdmin, navigate]);

    const environment = ENVIRONMENTS.find(e => e.id === envId) || ENVIRONMENTS[0];

    // Filter groups/endpoints by search
    const filteredGroups = API_GROUPS.map(group => {
        if (!search.trim()) return group;
        const q = search.toLowerCase();
        const filtered = group.endpoints.filter(ep =>
            ep.name.toLowerCase().includes(q) ||
            ep.path.toLowerCase().includes(q) ||
            (ep.description && ep.description.toLowerCase().includes(q))
        );
        return filtered.length > 0 ? { ...group, endpoints: filtered } : null;
    }).filter(Boolean);

    const scrollToGroup = useCallback((groupId) => {
        setActiveGroup(groupId);
        const el = groupRefs.current[groupId];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    if (!isSuperAdmin) return null;

    return (
        <div className="api-harness">
            {/* Top bar */}
            <header className="harness-topbar">
                <div className="harness-title">
                    <Terminal size={20} />
                    <span>API Test Harness</span>
                </div>

                {/* Environment selector */}
                <div className="env-pills">
                    {ENVIRONMENTS.map(env => (
                        <button
                            key={env.id}
                            className={`env-pill ${envId === env.id ? 'env-pill-active' : ''} ${env.dangerous ? 'env-pill-danger' : ''}`}
                            onClick={() => setEnvId(env.id)}
                            type="button"
                        >
                            {env.label}
                        </button>
                    ))}
                </div>

                {/* Production warning */}
                {environment.dangerous && (
                    <div className="prod-warning">
                        <AlertCircle size={14} />
                        <span>You are targeting <strong>Production</strong> — all actions are real</span>
                    </div>
                )}

                {/* Function key input */}
                <div className="function-key-wrap">
                    <Lock size={13} className="function-key-icon" />
                    <input
                        className="function-key-input"
                        type="password"
                        value={functionKey}
                        onChange={e => setFunctionKey(e.target.value)}
                        placeholder="Function key (for AuthorizationLevel.Function endpoints)"
                    />
                </div>
            </header>

            <div className="harness-layout">
                {/* Sidebar */}
                <aside className="harness-sidebar">
                    <div className="sidebar-search-wrap">
                        <Search size={14} className="sidebar-search-icon" />
                        <input
                            className="sidebar-search"
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Filter endpoints..."
                        />
                        {search && (
                            <button className="sidebar-search-clear" onClick={() => setSearch('')} type="button">
                                <X size={12} />
                            </button>
                        )}
                    </div>
                    <nav className="sidebar-nav">
                        {API_GROUPS.map(group => {
                            const visible = filteredGroups.find(g => g.id === group.id);
                            return (
                                <button
                                    key={group.id}
                                    className={`sidebar-item ${activeGroup === group.id ? 'sidebar-item-active' : ''} ${!visible ? 'sidebar-item-hidden' : ''}`}
                                    onClick={() => scrollToGroup(group.id)}
                                    type="button"
                                >
                                    <span className="sidebar-dot" />
                                    <GroupIcon name={group.icon} size={14} />
                                    <span className="sidebar-item-name">{group.name}</span>
                                    <span className="sidebar-item-count">
                                        {visible ? visible.endpoints.length : 0}
                                    </span>
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                {/* Main content */}
                <main className="harness-main">
                    {filteredGroups.length === 0 ? (
                        <div className="empty-state">
                            <Search size={32} />
                            <p>No endpoints match &ldquo;{search}&rdquo;</p>
                            <button className="empty-clear-btn" onClick={() => setSearch('')} type="button">
                                Clear search
                            </button>
                        </div>
                    ) : (
                        filteredGroups.map(group => (
                            <GroupSection
                                key={group.id}
                                group={group}
                                environment={environment}
                                functionKey={functionKey}
                                groupRef={el => { groupRefs.current[group.id] = el; }}
                            />
                        ))
                    )}
                </main>
            </div>
        </div>
    );
}

export default ApiTestHarness;
